import React, { lazy, Suspense } from 'react'
import type { Icon18 } from 'kidsout:icons'
import { defineMessages } from 'react-intl'

import { createSelector } from '@app/utils/createSelector'
import { pathIsSubPathOf } from '@app/utils/url'

import { intlSelector } from '@app/store/slices/intl'

import { ControlClasses } from '@app/components/ControlClasses/ControlClasses'
import type { NavItemTitleComponent, NavMenuItem } from '@app/components/LayoutMilk/navItemsUtils'
import { intlFormatters, NavItemVisibility } from '@app/components/LayoutMilk/navItemsUtils'

import type { AcademyEventName } from './config'
import { URLS } from './config'

export const createAcademyNavigationSelector = (eventSelector: () => AcademyEventName) =>
  createSelector([eventSelector, intlSelector], (event, intl): NavMenuItem => {
    const { formatMessage } = intl

    return {
      type: 'item',
      id: event,
      icon: headerIcons[event] || headerIcons.academy,
      url: URLS.main(event),
      matches: path => path !== URLS.cart(event) && pathIsSubPathOf(URLS.main(event), path),
      title: formatMessage(messages[`label__${event}`] || messages.label__academy, intlFormatters),
      visibility: event === 'academy' ? NavItemVisibility.Footer | NavItemVisibility.Side : NavItemVisibility.None,
      children: [
        {
          type: 'item',
          id: `${event}_main`,
          url: URLS.main(event),
          title: formatMessage(messages.label_main),
        },
        {
          type: 'item',
          id: `${event}_memo`,
          url: '/memo',
          title: formatMessage(messages.label_memo),
        },
        {
          type: 'item',
          id: `${event}_purchased`,
          url: URLS.purchased(event),
          title: formatMessage(messages.label_purchased),
        },
        {
          type: 'item',
          id: `${event}_cart`,
          url: URLS.cart(event),
          wrapInLink: false,
          title: HeaderCardNavItem,
        },
      ],
    }
  })

// eslint-disable-next-line boundaries/dependencies -- academy nav lazily injects the Academy cart button via its own chunk
const HeaderCartButton = lazy(() => import('@app/routes/Academy/HeaderCartButton').then(m => ({ default: m.HeaderCartButton })))
const HeaderCardNavItem: NavItemTitleComponent = ({ type }) => {
  return (
    <div className={type === 'header' ? ControlClasses.hide({ mobile: true, tablet: true }) : undefined} data-cart-header-pointer>
      <Suspense>
        <HeaderCartButton type={type} />
      </Suspense>
    </div>
  )
}

const messages = defineMessages({
  label__academy: { id: "utils.academy.navigationSelector.messages.label__academy.c9cd", defaultMessage: 'Онлайн-{zspace}занятия' },
  label__new_year_party: { id: "utils.academy.navigationSelector.messages.label__new_year_party.d8f7", defaultMessage: 'Утренники' },
  label_main: { id: "utils.academy.navigationSelector.messages.label_main.790a", defaultMessage: 'Расписание' },
  label_memo: { id: "utils.academy.navigationSelector.messages.label_memo.f162", defaultMessage: 'Памятка онлайн-занятий' },
  label_purchased: { id: "utils.academy.navigationSelector.messages.label_purchased.b047", defaultMessage: 'Мои занятия' },
})

const headerIcons: { [K in AcademyEventName]?: Icon18 } = {
  academy: 'smile',
  new_year_party: 'tree2',
}
